import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Printer, Loader2, AlertCircle, RotateCw, Check, BadgeCheck, Pencil, ChevronLeft, ChevronRight, AlertTriangle, Info, File } from 'lucide-react';
import './DocumentPreview.css';

/** Severity vocabulary mirrors the worker's L0 doc-side validator
 *  (``worker/src/steps/extract_facts.py:_compute_validation_block``). */
export type PreviewFactValidationSeverity = 'hard' | 'soft' | 'autofix';

export interface PreviewFactValidation {
  severity: PreviewFactValidationSeverity;
  /** Human-readable rule description shown in the tooltip / aria-label. */
  message: string;
  /** Stable id from ``hofsync_ontology.validators`` — sent back to the
   *  backend on correction so the audit event pins which rule was satisfied. */
  validator_id: string;
  /** When present, the validator suggests a deterministic replacement
   *  the operator can apply with one click. */
  autofix_value?: unknown;
}

/**
 * One column of a repeating-row (Buchungen) fact table (#1525). ``key`` indexes
 * into each row object; ``label`` is the (German) header. The column *model* —
 * order + headers — is supplied by the consumer (DocumentsPage), keeping the
 * design system domain-agnostic (mirrors how ``classification`` / ``taxonomyDomains``
 * are passed in). ``numeric`` right-aligns the cell for money columns
 * (``amount`` / ``balance``); the values are pre-formatted German strings
 * (e.g. ``"-120,00"``) and are rendered verbatim, never reparsed.
 */
export interface PreviewFactColumn {
  key: string;
  label: string;
  numeric?: boolean;
}

export interface PreviewFact {
  field: string;
  label: string;
  value: string;
  overridden?: boolean;
  type?: 'text' | 'date';
  /**
   * L0 validation finding for this field. When set, the row gets a
   * severity-tinted background and a badge that reveals the rule's
   * message on hover/focus. Operators clicking the value will route
   * through the *correction* path (``POST /facts/correct``) rather
   * than the generic override path — the consumer is responsible for
   * branching on this in its ``onFactEdit`` callback.
   */
  validation?: PreviewFactValidation;
  /**
   * Structured rows for a classifier-v3 repeating-row field — ``line_items``
   * (the Buchungen on a bank statement, #1525/#1498). When present, the fact
   * renders as a compact **non-editable** table (one ``<tr>`` per row, columns
   * driven by ``columns``) instead of the scalar value row — a repeating-row
   * fact has no scalar override affordance, so this also closes #1527's FE half.
   * ``value`` is kept populated with the ``"N Positionen"`` summary as the
   * a11y/empty-state fallback. Every row key is optional (a sparse statement
   * row may omit ``reference`` / ``balance``) — missing cells render empty.
   */
  rows?: Array<Record<string, string | null | undefined>>;
  /**
   * Column model (order + German headers) for ``rows``. Required for the table
   * to render; when ``rows`` is set but ``columns`` is empty/absent the fact
   * falls back to the scalar value row.
   */
  columns?: PreviewFactColumn[];
}

/** Inline badge showing the L0 validator finding next to a fact label.
 *  Icon distinguishes severity without colour-only encoding (a11y);
 *  the message is exposed via ``title`` and ``aria-label`` so screen
 *  readers and tooltip-on-hover both surface the rule text. */
const ValidationBadge: React.FC<{ validation: PreviewFactValidation }> = ({ validation }) => {
  const Icon = validation.severity === 'hard'
    ? AlertCircle
    : validation.severity === 'soft'
      ? AlertTriangle
      : Info;
  const ariaLabel = `${validation.severity}: ${validation.message}`;
  return (
    <span
      role="img"
      aria-label={ariaLabel}
      title={validation.message}
      data-validator-id={validation.validator_id}
      className={`document-preview-fact-validation-badge document-preview-fact-validation-badge--${validation.severity}`}
    >
      <Icon size={12} aria-hidden="true" />
    </span>
  );
};

/** Inline autocomplete dropdown for fact fields whose ontology mapping
 *  points at a canonical entity (e.g. ``supplier_name`` → ``supplier``).
 *  #386 subtask 05 — keeps the same Enter-saves / Escape-cancels keyboard
 *  contract as the plain ``<input>`` branch and adds an arrow-key menu
 *  navigation. Picking a row sets the ``picked_entity_id`` that flows
 *  through ``onFactEdit`` so the worker recalc bypasses dedup. */
const EntityAutocomplete: React.FC<{
  entityType: string;
  value: string;
  onChange: (value: string) => void;
  onPick: (entity: { entity_id: string; name: string }) => void;
  onCommit: () => void;
  onCancel: () => void;
  fetch: (entityType: string, q: string) => Promise<Array<{ entity_id: string; name: string }>>;
  // React 19's `useRef<T>(null)` yields `RefObject<T | null>`, so the prop has
  // to admit the null to stay assignable from a plain `useRef` handle.
  inputRef?: React.RefObject<HTMLInputElement | null>;
  disabled?: boolean;
  saving?: boolean;
}> = ({ entityType, value, onChange, onPick, onCommit, onCancel, fetch, inputRef, disabled, saving }) => {
  const [items, setItems] = useState<Array<{ entity_id: string; name: string }>>([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef<HTMLSpanElement>(null);

  // Debounced fetch on value change.
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const rows = await fetch(entityType, value);
        if (!cancelled) {
          setItems(rows);
          setOpen(rows.length > 0);
          setActiveIdx(rows.length > 0 ? 0 : -1);
        }
      } catch {
        if (!cancelled) {
          setItems([]);
          setOpen(false);
        }
      }
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [entityType, value, fetch]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && open) {
      e.preventDefault();
      setActiveIdx(idx => Math.min(items.length - 1, idx + 1));
    } else if (e.key === 'ArrowUp' && open) {
      e.preventDefault();
      setActiveIdx(idx => Math.max(0, idx - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && activeIdx >= 0 && items[activeIdx]) {
        onPick(items[activeIdx]);
      } else {
        onCommit();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (open) {
        setOpen(false);
      } else {
        onCancel();
      }
    }
  };

  return (
    <span ref={containerRef} className="document-preview-fact-autocomplete">
      <input
        ref={inputRef}
        type="text"
        className="document-preview-fact-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Close on blur — but if blur was caused by clicking a menu row,
          // the row's onMouseDown already fired and set the pick.
          setTimeout(() => setOpen(false), 100);
        }}
        disabled={disabled}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls="document-preview-fact-autocomplete-list"
      />
      {open && items.length > 0 && (
        <ul
          id="document-preview-fact-autocomplete-list"
          className="document-preview-fact-autocomplete-menu"
          role="listbox"
        >
          {items.map((it, idx) => (
            <li
              key={it.entity_id}
              role="option"
              aria-selected={idx === activeIdx}
              className={
                idx === activeIdx
                  ? 'document-preview-fact-autocomplete-item document-preview-fact-autocomplete-item--active'
                  : 'document-preview-fact-autocomplete-item'
              }
              onMouseDown={(e) => {
                // preventDefault so the input keeps focus and blur doesn't pre-empt
                e.preventDefault();
                onPick(it);
              }}
            >
              {it.name}
            </li>
          ))}
        </ul>
      )}
      {saving && (
        <span className="document-preview-fact-saving" role="status" aria-live="polite">
          saving…
        </span>
      )}
    </span>
  );
};

export interface PreviewClassification {
  category: string | null;
  subcategory: string | null;
  document_type: string | null;
  manually_classified?: boolean;
  /**
   * classifier-v3 native two-axis cell + version (#1066). ``domain``/``role`` are nullable —
   * a cell-less (not-yet-classified) doc carries ``null`` for both. The classification header
   * + two-axis override render whenever ``classifierV3Enabled`` is set (the legacy
   * ``category/subcategory/document_type`` picker was retired in #1065); a cell-less doc shows an
   * "unclassified" placeholder plus the domains picker so an operator can assign the cell. The
   * legacy ``category``/``subcategory``/``document_type`` fields remain on the shape (the backend
   * still dual-writes them) but are no longer rendered.
   */
  domain?: string | null;
  role?: string | null;
  classifier_version?: string | null;
}

/** classifier-v3 (domain → role) taxonomy options for the two-axis override (#1066). */
export interface TaxonomyRoleOption {
  id: string;
  name: string;
  tier: number;
}

export interface TaxonomyDomainOption {
  id: string;
  name: string;
  roles: TaxonomyRoleOption[];
}

export interface DocumentPreviewProps {
  /** Whether the preview is open */
  isOpen: boolean;
  /** URL to the document to preview (blob URL or direct URL) */
  documentUrl: string | null;
  /** Document filename for display and download */
  filename: string;
  /** Handler when close is clicked or Escape pressed */
  onClose: () => void;
  /** Handler when download is clicked */
  onDownload?: () => void;
  /** Handler when print is clicked */
  onPrint?: () => void;
  /** Whether the document is loading */
  loading?: boolean;
  /** Error message if document failed to load */
  error?: string | null;
  /** Label for close button (for i18n) */
  closeLabel?: string;
  /** Label for download button (for i18n) */
  downloadLabel?: string;
  /** Label for print button (for i18n) */
  printLabel?: string;
  /** Handler when rotate is clicked */
  onRotate?: (degrees: 90 | 180 | 270) => void;
  /** 1-indexed page to open the PDF on (#755). When set, the iframe
   *  src is suffixed with ``#page=N`` so the native PDF viewer scrolls
   *  to that page on load. Ignored for non-PDF files. */
  initialPage?: number | null;
  /** Whether rotation is in progress */
  rotating?: boolean;
  /** Label for rotate button (for i18n) */
  rotateLabel?: string;
  /** Label for "Back to documents" (for i18n) */
  backLabel?: string;
  /** Label shown while loading (for i18n) */
  loadingLabel?: string;
  /** Message shown in the placeholder when the file type cannot be
   *  previewed inline (e.g. ``.xlsx``/``.docx``/``.csv``) (#1890). Follows the
   *  label-prop pattern (English default; the frontend passes the German
   *  ``t('documents.noInlinePreview')``). The DS stays i18n-agnostic. */
  noPreviewLabel?: string;
  /** Optional class name */
  className?: string;

  // --- Facts panel props (all optional for backward compatibility) ---

  /** Extracted facts to display in the side panel */
  facts?: PreviewFact[] | null;
  /** Whether facts are loading */
  factsLoading?: boolean;
  /** Current document classification */
  classification?: PreviewClassification | null;

  // --- classifier-v3 two-axis surface (#1066) — all optional/additive ---

  /** Classifier-v3 gate, evaluated by the consumer (the design system stays pure). The
   *  classification breadcrumb + two-axis (domain → role) override + version badge render when
   *  this is true; the legacy category/subcategory/type picker was retired (#1065), so a doc
   *  with no cell shows an "unclassified" placeholder plus the domains picker rather than a
   *  legacy breadcrumb. In practice the frontend hardcodes this on. */
  classifierV3Enabled?: boolean;
  /** v3 (domain → role) taxonomy tree for the two-axis override dropdowns + name resolution. */
  taxonomyDomains?: TaxonomyDomainOption[];
  /** Handler when the user overrides the v3 (domain, role) cell. */
  onClassificationOverride?: (cell: { domain: string; role: string }) => void;
  /** Whether the v3 classification override is in progress. */
  classificationOverrideLoading?: boolean;
  /** Handler when a fact value is edited inline.
   *  ``pickedEntityId`` is set (#386 subtask 05) when the operator picked an
   *  existing canonical entity from the autocomplete dropdown — flows through
   *  to the backend as ``picked_entity_id_by_field`` so the worker recalc
   *  bypasses L1-L4 dedup. */
  onFactEdit?: (fieldId: string, value: string, pickedEntityId?: string) => void;
  /** Save mutation status — drives the inline saving affordance and disables the ✓ button */
  factSaveStatus?: 'idle' | 'saving' | 'success' | 'error';
  /** Field id currently being saved (matches PreviewFact.field). Null/undefined when no save in flight. */
  savingFieldId?: string | null;
  /** #386 subtask 05 — fact field id → canonical entity_type. Fields in this
   *  map render an autocomplete dropdown sourced from existing canonical
   *  entities for the tenant. Operator picks "FRUTANIA GmbH" instead of
   *  typing it; the picked entity_id rides on ``onFactEdit`` so the worker
   *  short-circuits dedup. */
  entityFieldMap?: Record<string, string>;
  /** #386 subtask 05 — async source for the autocomplete dropdown. Called
   *  on each keystroke (caller responsible for debounce / caching). Must
   *  resolve to an array shaped ``[{entity_id, name}, ...]``. */
  fetchEntitySuggestions?: (
    entityType: string,
    q: string,
  ) => Promise<Array<{ entity_id: string; name: string }>>;

  // --- Navigation props (all optional — absent means single-doc context, no arrows) ---

  /** Ordered list of document IDs. Navigation arrows shown only when length ≥ 2. */
  documentList?: string[];
  /** Index of the currently displayed document in documentList */
  currentIndex?: number;
  /** Called when user navigates to a different document */
  onNavigate?: (newIndex: number) => void;
  /** Whether prev navigation is allowed (disabled at first item) */
  canNavigatePrev?: boolean;
  /** Whether next navigation is allowed (disabled at last item) */
  canNavigateNext?: boolean;

  // --- Verified badge slot (#495) ---

  /** Optional slot rendered in the classification header next to the type
   *  segments. Consumers gate on flag + admin + impersonation; the design
   *  system component itself stays pure and renders whatever node is passed. */
  verifiedBadge?: React.ReactNode;

  // --- Twin links slot (FR-017.7) ---

  /** Optional slot rendered at the foot of the facts panel: what this
   *  document produced in the digital twin ("Verknüpfte Objekte"). Same
   *  contract as `verifiedBadge` — the design-system component stays pure
   *  and renders whatever node the consumer passes, so it takes no data
   *  dependency on the twin API. */
  twinLinksSlot?: React.ReactNode;
}

/**
 * Full-screen document preview overlay.
 *
 * Displays documents (PDFs, images) in a distraction-free fullscreen viewer
 * with minimal controls: download, print, and close.
 *
 * When `facts` is provided, renders a two-column layout with a facts panel
 * on the right side. The facts panel also supports classifier-v3 two-axis
 * `(domain → role)` classification override via cascading dropdowns when
 * `taxonomyDomains` and `onClassificationOverride` are provided (#1066); the
 * legacy category/subcategory/type picker was retired in #1065.
 */
export const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  isOpen,
  documentUrl,
  filename,
  onClose,
  onDownload,
  onPrint,
  onRotate,
  rotating = false,
  loading = false,
  error = null,
  closeLabel: _closeLabel = 'Close',
  downloadLabel = 'Download',
  printLabel = 'Print',
  rotateLabel = 'Rotate',
  backLabel = 'Back to documents',
  loadingLabel = 'Loading document...',
  noPreviewLabel = 'Preview not available for this file type',
  className = '',
  facts = null,
  factsLoading = false,
  classification = null,
  classifierV3Enabled = false,
  taxonomyDomains,
  onClassificationOverride,
  classificationOverrideLoading = false,
  onFactEdit,
  factSaveStatus: _factSaveStatus = 'idle',
  savingFieldId = null,
  entityFieldMap,
  fetchEntitySuggestions,
  documentList,
  currentIndex = -1,
  onNavigate,
  canNavigatePrev = false,
  canNavigateNext = false,
  verifiedBadge = null,
  twinLinksSlot = null,
  initialPage = null,
}) => {
  const showNavigation = !!documentList && documentList.length >= 2 && !!onNavigate;
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [pickedEntityId, setPickedEntityId] = useState<string | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  /** The value the editor opened with, so a save that changed nothing can be dropped.
   *  Held in a ref rather than state: it is only read at commit time and must not
   *  re-render the row when an edit starts. */
  const editOriginalValueRef = useRef('');
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [confirmingOverride, setConfirmingOverride] = useState(false);
  // classifier-v3 two-axis override selection (#1066).
  const [selectedDomain, setSelectedDomain] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  const hasFacts = facts !== null || factsLoading;

  // Handle keyboard events (Escape to close, ArrowLeft/Right for navigation)
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && onNavigate && canNavigatePrev) {
        onNavigate(currentIndex - 1);
      } else if (e.key === 'ArrowRight' && onNavigate && canNavigateNext) {
        onNavigate(currentIndex + 1);
      }
    },
    [onClose, onNavigate, canNavigatePrev, canNavigateNext, currentIndex]
  );

  // Set up keyboard listener and body scroll lock
  useEffect(() => {
    if (!isOpen) return;

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Reset iframe loaded state when URL changes
  useEffect(() => {
    setIframeLoaded(false);
  }, [documentUrl]);

  // Reset override state when the previewed document changes. Keyed on ``documentUrl`` (the
  // per-document identity, same key the iframe-reset effect above uses) as well as the cell,
  // so navigating between two cell-less docs — both with an all-null classification tuple, now
  // reachable since #1065 renders the picker for cell-less docs — still discards any pending,
  // possibly mid-confirm, selection instead of firing it against the newly-shown document.
  useEffect(() => {
    setOverrideOpen(false);
    setConfirmingOverride(false);
    setSelectedDomain('');
    setSelectedRole('');
  }, [
    documentUrl,
    classification?.category,
    classification?.subcategory,
    classification?.document_type,
    classification?.domain,
    classification?.role,
  ]);

  // Handle print action
  const handlePrint = useCallback(() => {
    if (onPrint) {
      onPrint();
    } else if (documentUrl) {
      const iframe = document.querySelector('.document-preview-iframe') as HTMLIFrameElement;
      if (iframe?.contentWindow) {
        iframe.contentWindow.print();
      }
    }
  }, [onPrint, documentUrl]);

  // --- classifier-v3 two-axis surface (#1066); legacy type-picker retired (#1065) ---

  /** The classification surface is active whenever the classifier-v3 gate is on. The legacy
   *  category/subcategory/type picker was retired (#1065): a doc with no native cell shows an
   *  "unclassified" placeholder + the domains picker rather than a legacy breadcrumb. */
  const v3Active = !!classifierV3Enabled;

  // Resolve the v3 (domain, role) ids to display names via the v3 taxonomy.
  const v3ClassificationNames = useMemo(() => {
    if (!classification?.domain) return null;
    const domainNode = taxonomyDomains?.find(d => d.id === classification.domain);
    const roleNode = domainNode?.roles.find(r => r.id === classification.role);
    return {
      domain: domainNode?.name ?? classification.domain,
      role: roleNode?.name ?? classification.role,
    };
  }, [classification?.domain, classification?.role, taxonomyDomains]);

  // Roles available for the selected domain (cascading second selector).
  const roleOptions = useMemo(() => {
    if (!taxonomyDomains || !selectedDomain) return [];
    return taxonomyDomains.find(d => d.id === selectedDomain)?.roles ?? [];
  }, [taxonomyDomains, selectedDomain]);

  const handleDomainChange = useCallback((domainId: string) => {
    setSelectedDomain(domainId);
    setSelectedRole('');
    setConfirmingOverride(false);
  }, []);

  const handleRoleChange = useCallback((roleId: string) => {
    setSelectedRole(roleId);
    setConfirmingOverride(false);
  }, []);

  const handleClassificationSubmit = useCallback(() => {
    if (!confirmingOverride) {
      setConfirmingOverride(true);
      return;
    }
    if (onClassificationOverride && selectedDomain && selectedRole) {
      onClassificationOverride({ domain: selectedDomain, role: selectedRole });
      setConfirmingOverride(false);
    }
  }, [confirmingOverride, onClassificationOverride, selectedDomain, selectedRole]);

  const handleFactEditStart = useCallback((field: string, currentValue: string, factType?: string) => {
    setEditingField(field);
    setPickedEntityId(null);
    // For date fields, convert DD.MM.YYYY display format to YYYY-MM-DD for the date input
    let initialValue: string;
    if (factType === 'date' && currentValue) {
      const parts = currentValue.split('.');
      initialValue = parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : currentValue;
    } else {
      initialValue = currentValue || '';
    }
    setEditValue(initialValue);
    editOriginalValueRef.current = initialValue;
    // Focus input on next tick
    setTimeout(() => editInputRef.current?.focus(), 0);
  }, []);

  const handleFactEditSave = useCallback(() => {
    // Drop a commit that changes nothing. Every save used to fire a write, and a native
    // `<input type="date">` reports `value === ''` for any incomplete or invalid entry — so
    // touching an empty date row and confirming it posted `"" -> ""`. Prod document
    // `f7de805f-…_att_0` collected three such no-op `document.fact_overridden` rows in one
    // session. The guard keys on UNCHANGED, never on empty: clearing a populated field is a
    // real edit and still goes through. An entity pick is always delivered — it carries an
    // `entity_id` the caller needs even when the visible name is identical.
    const unchanged = editValue === editOriginalValueRef.current && pickedEntityId === null;
    if (editingField && onFactEdit && !unchanged) {
      onFactEdit(editingField, editValue, pickedEntityId ?? undefined);
    }
    setEditingField(null);
    setEditValue('');
    setPickedEntityId(null);
  }, [editingField, editValue, pickedEntityId, onFactEdit]);

  const handleFactEditCancel = useCallback(() => {
    setEditingField(null);
    setEditValue('');
    setPickedEntityId(null);
  }, []);

  const handleFactEditKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFactEditSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleFactEditCancel();
    }
  }, [handleFactEditSave, handleFactEditCancel]);

  // Determine if the document is an image
  const isImage = filename.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i);
  const isPdf = filename.match(/\.pdf$/i);
  // Only images and PDFs can be rendered inline by the browser. Everything else
  // (``.xlsx``/``.docx``/``.csv``/``.pptx``/…) must NOT be pointed at by an
  // ``<iframe src=blobUrl>`` — Chrome force-downloads a blob it can't render
  // inline, which is the auto-download-on-open bug (#1890). Non-previewable types
  // render a placeholder instead; the top-bar Download button stays the download
  // affordance.
  const isPreviewable = !!(isImage || isPdf);

  // For PDFs, append parameters to hide the native toolbar. ``initialPage``
  // appends ``&page=N`` so the native PDF viewer deep-links to that page on
  // open (#755). When null, the default behaviour (page 1) is preserved.
  const pdfViewerUrl = documentUrl && isPdf
    ? `${documentUrl}#toolbar=0&navpanes=0&scrollbar=1${
        initialPage != null && initialPage > 1 ? `&page=${initialPage}` : ''
      }`
    : documentUrl;

  if (!isOpen) return null;

  const content = (
    <div
      className={`document-preview-overlay ${className}`}
      role="dialog"
      aria-modal="true"
      aria-label={filename}
    >
      {/* Minimal top bar */}
      <div className="document-preview-topbar">
        <div className="document-preview-topbar-left">
          <button
            type="button"
            className="document-preview-back-button"
            onClick={onClose}
            aria-label={backLabel}
          >
            <X className="document-preview-icon" />
            <span className="document-preview-back-text">{backLabel}</span>
          </button>
          {showNavigation && (
            <div className="document-preview-nav-arrows">
              <button
                type="button"
                className="document-preview-nav-button"
                onClick={() => onNavigate!(currentIndex - 1)}
                disabled={!canNavigatePrev}
                aria-label="Previous document"
                title="Previous document (←)"
              >
                <ChevronLeft className="document-preview-icon" />
              </button>
              <button
                type="button"
                className="document-preview-nav-button"
                onClick={() => onNavigate!(currentIndex + 1)}
                disabled={!canNavigateNext}
                aria-label="Next document"
                title="Next document (→)"
              >
                <ChevronRight className="document-preview-icon" />
              </button>
            </div>
          )}
        </div>

        <div className="document-preview-topbar-center">
          <span className="document-preview-filename">{filename}</span>
        </div>

        <div className="document-preview-topbar-right">
          {onDownload && (
            <button
              type="button"
              className="document-preview-action-button"
              onClick={onDownload}
              aria-label={downloadLabel}
              title={downloadLabel}
            >
              <Download className="document-preview-icon" />
            </button>
          )}
          <button
            type="button"
            className="document-preview-action-button"
            onClick={handlePrint}
            aria-label={printLabel}
            title={printLabel}
          >
            <Printer className="document-preview-icon" />
          </button>
          {onRotate && (
            <button
              type="button"
              className={`document-preview-action-button ${rotating ? 'document-preview-action-button--rotating' : ''}`}
              onClick={() => onRotate(90)}
              aria-label={rotateLabel}
              title={rotateLabel}
              disabled={rotating}
            >
              <RotateCw className="document-preview-icon" />
            </button>
          )}
        </div>
      </div>

      {/* Document content area */}
      <div className={`document-preview-content ${hasFacts ? 'document-preview-content--with-facts' : ''}`}>
        {/* Document viewer */}
        <div className="document-preview-viewer">
          {/* Loading state. Gated on ``isPreviewable`` (#1890): the placeholder
              branch never fires an ``<img>``/``<iframe>`` onLoad, so ``iframeLoaded``
              would stay false forever and a never-resolving spinner would sit on top
              of the placeholder. An explicit ``loading`` prop still shows the spinner
              regardless of type (the blob fetch is in flight). */}
          {(loading || (!iframeLoaded && documentUrl && !error && isPreviewable)) && (
            <div className="document-preview-loading">
              <Loader2 className="document-preview-spinner" />
              <span>{loadingLabel}</span>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="document-preview-error">
              <AlertCircle className="document-preview-error-icon" />
              <span>{error}</span>
            </div>
          )}

          {/* Document viewer. Three branches (#1890): image → <img>, pdf → <iframe>,
              anything else → placeholder. NEVER point an <iframe> at a blob the browser
              can't render inline — that is the force-download-on-open bug. */}
          {documentUrl && !error && (
            <>
              {isImage ? (
                <img
                  src={documentUrl}
                  alt={filename}
                  className="document-preview-image"
                  onLoad={() => setIframeLoaded(true)}
                />
              ) : isPdf ? (
                <iframe
                  src={pdfViewerUrl ?? undefined}
                  className={`document-preview-iframe ${iframeLoaded ? 'document-preview-iframe--loaded' : ''}`}
                  title={filename}
                  onLoad={() => setIframeLoaded(true)}
                />
              ) : (
                <div className="document-preview-no-preview" role="status">
                  <File className="document-preview-no-preview-icon" />
                  <span className="document-preview-no-preview-filename">{filename}</span>
                  <span className="document-preview-no-preview-message">{noPreviewLabel}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Facts panel */}
        {hasFacts && (
          <div className="document-preview-facts-panel">
            {/* Classification header with inline override (classifier-v3 two-axis; #1066).
                The legacy category/subcategory/type breadcrumb + picker was retired (#1065):
                a cell-less doc shows an "unclassified" placeholder instead. */}
            {classification && v3Active && (
              <div className="document-preview-type-header">
                <div className="document-preview-type-breadcrumb">
                  {v3ClassificationNames ? (
                    <>
                      {v3ClassificationNames.domain && (
                        <span className="document-preview-type-segment">{v3ClassificationNames.domain}</span>
                      )}
                      {v3ClassificationNames.role && (
                        <>
                          <span className="document-preview-type-separator">›</span>
                          <span className="document-preview-type-segment document-preview-type-segment--leaf">{v3ClassificationNames.role}</span>
                        </>
                      )}
                    </>
                  ) : (
                    <span className="document-preview-type-segment document-preview-type-segment--unclassified">Nicht klassifiziert</span>
                  )}
                  {classification.manually_classified && (
                    <BadgeCheck size={13} className="document-preview-type-badge" />
                  )}
                  {classification.classifier_version === 'v3' && (
                    <span className="document-preview-version-badge" title="Klassifiziert mit Modell v3">v3</span>
                  )}
                  {verifiedBadge && (
                    <span className="document-preview-verified-badge-slot">{verifiedBadge}</span>
                  )}
                </div>

                {onClassificationOverride && taxonomyDomains && (
                  <button
                    type="button"
                    className="document-preview-type-edit"
                    onClick={() => setOverrideOpen(!overrideOpen)}
                  >
                    <span>{overrideOpen ? 'Abbrechen' : 'Ändern'}</span>
                  </button>
                )}
              </div>
            )}

            {/* Override form — classifier-v3 two-axis (domain → role) (#1066) */}
            {overrideOpen && onClassificationOverride && taxonomyDomains && (
              <div className="document-preview-override-form">
                <select
                  className="document-preview-override-select"
                  value={selectedDomain}
                  onChange={(e) => handleDomainChange(e.target.value)}
                  disabled={classificationOverrideLoading}
                  aria-label="Bereich wählen"
                >
                  <option value="">Bereich wählen…</option>
                  {taxonomyDomains.map(domain => (
                    <option key={domain.id} value={domain.id}>{domain.name}</option>
                  ))}
                </select>

                {selectedDomain && (
                  <select
                    className="document-preview-override-select"
                    value={selectedRole}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    disabled={classificationOverrideLoading}
                    aria-label="Rolle wählen"
                  >
                    <option value="">Rolle wählen…</option>
                    {roleOptions.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                )}

                {selectedRole && (
                  <button
                    type="button"
                    className={`document-preview-override-submit ${confirmingOverride ? 'document-preview-override-submit--confirm' : ''}`}
                    onClick={handleClassificationSubmit}
                    disabled={classificationOverrideLoading}
                  >
                    {classificationOverrideLoading ? (
                      <Loader2 size={14} className="document-preview-spinner" />
                    ) : (
                      <Check size={14} />
                    )}
                    <span>{confirmingOverride ? 'Wirklich ändern?' : 'Klassifizierung überschreiben'}</span>
                  </button>
                )}
              </div>
            )}

            {/* Facts list */}
            <div className="document-preview-facts-list">
              {factsLoading && (
                <div className="document-preview-facts-loading">
                  <Loader2 size={16} className="document-preview-spinner" />
                  <span>Felder werden geladen…</span>
                </div>
              )}

              {facts && facts.length === 0 && !factsLoading && (
                <div className="document-preview-facts-empty">
                  Keine extrahierten Felder vorhanden.
                </div>
              )}

              {facts && facts.map((fact) => {
                // Repeating-row (Buchungen) fact — render a compact, NON-editable
                // table (#1525). A repeating-row field has no scalar override
                // affordance (no onClick / inline input / Pencil), which also
                // satisfies #1527's read-only requirement. The column model is
                // supplied by the consumer; values are pre-formatted strings
                // rendered verbatim. Missing keys render an empty cell (the
                //   idiom keeps the cell from collapsing) rather than
                // "undefined". Skip the table only if columns are absent —
                // then fall through to the scalar row using ``value``.
                if (fact.rows && fact.columns && fact.columns.length > 0) {
                  const { rows, columns } = fact;
                  return (
                    // ``tabIndex=0`` + ``role=group`` make the horizontal-scroll
                    // wrapper keyboard-reachable so overflowed columns aren't
                    // unreachable for keyboard-only users on narrow viewports.
                    <div
                      key={fact.field}
                      className="document-preview-fact-table-wrap"
                      role="group"
                      aria-label={fact.label}
                      tabIndex={0}
                    >
                      <table className="document-preview-fact-table">
                        <caption className="document-preview-fact-table-caption">
                          {fact.label}
                          {fact.validation && (
                            <ValidationBadge validation={fact.validation} />
                          )}
                        </caption>
                        <thead>
                          <tr>
                            {columns.map((col) => (
                              <th
                                key={col.key}
                                scope="col"
                                className={
                                  col.numeric
                                    ? 'document-preview-fact-table-cell--numeric'
                                    : undefined
                                }
                              >
                                {col.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, rowIdx) => (
                            <tr key={rowIdx}>
                              {columns.map((col) => {
                                const cell = row[col.key];
                                const text =
                                  cell === null || cell === undefined || cell === ''
                                    ? ' '
                                    : String(cell);
                                return (
                                  <td
                                    key={col.key}
                                    className={
                                      col.numeric
                                        ? 'document-preview-fact-table-cell--numeric'
                                        : undefined
                                    }
                                  >
                                    {text}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                }

                // Format date values as DD.MM.YYYY for display
                const displayValue = fact.type === 'date' && fact.value
                  ? (() => {
                      const parts = fact.value.split('-');
                      return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : fact.value;
                    })()
                  : fact.value;

                // L0 validation finding (#706). The row tint signals
                // severity at a glance; the inline badge carries the
                // validator's message + an icon distinguishing
                // hard/soft/autofix without colour-only encoding.
                const validation = fact.validation;
                const rowClasses = [
                  'document-preview-fact-row',
                  fact.overridden && 'document-preview-fact-row--overridden',
                  validation && `document-preview-fact-row--validation-${validation.severity}`,
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <div
                    key={fact.field}
                    className={rowClasses}
                  >
                    <span className="document-preview-fact-label">
                      {fact.label}
                      {validation && (
                        <ValidationBadge validation={validation} />
                      )}
                    </span>
                    {editingField === fact.field ? (
                      <span className="document-preview-fact-edit">
                        {entityFieldMap?.[fact.field] && fetchEntitySuggestions ? (
                          <EntityAutocomplete
                            entityType={entityFieldMap[fact.field]}
                            value={editValue}
                            onChange={(v) => {
                              setEditValue(v);
                              // Typing invalidates a previous pick.
                              setPickedEntityId(null);
                            }}
                            onPick={(entity) => {
                              // Commit immediately on pick — bypass the
                              // setEditValue/setPickedEntityId state-update
                              // round-trip (which would land in a stale
                              // closure inside handleFactEditSave). Operator's
                              // choice is final, so emit the values directly.
                              if (onFactEdit) {
                                onFactEdit(fact.field, entity.name, entity.entity_id);
                              }
                              setEditingField(null);
                              setEditValue('');
                              setPickedEntityId(null);
                            }}
                            onCommit={handleFactEditSave}
                            onCancel={handleFactEditCancel}
                            fetch={fetchEntitySuggestions}
                            inputRef={editInputRef}
                            disabled={savingFieldId === fact.field}
                            saving={savingFieldId === fact.field}
                          />
                        ) : (
                          <input
                            ref={editInputRef}
                            type={fact.type === 'date' ? 'date' : 'text'}
                            className="document-preview-fact-input"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handleFactEditSave}
                            onKeyDown={handleFactEditKeyDown}
                          />
                        )}
                        <button
                          type="button"
                          aria-label="Save fact"
                          className="document-preview-fact-save"
                          disabled={savingFieldId === fact.field}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleFactEditSave}
                        >
                          <Check size={14} aria-hidden="true" />
                        </button>
                        {!entityFieldMap?.[fact.field] && savingFieldId === fact.field && (
                          <span className="document-preview-fact-saving" role="status" aria-live="polite">
                            saving…
                          </span>
                        )}
                      </span>
                    ) : (
                      <span
                        className={`document-preview-fact-value ${onFactEdit ? 'document-preview-fact-value--editable' : ''}`}
                        onClick={onFactEdit ? () => handleFactEditStart(fact.field, displayValue, fact.type) : undefined}
                        title={onFactEdit ? 'Klicken zum Bearbeiten' : undefined}
                      >
                        {displayValue || '\u00A0'}
                        {savingFieldId === fact.field && (
                          <span
                            className="document-preview-fact-saving"
                            role="status"
                            aria-live="polite"
                            style={{ marginLeft: '0.5em', opacity: 0.7, fontSize: '0.85em' }}
                          >
                            saving\u2026
                          </span>
                        )}
                        {onFactEdit && <Pencil size={12} className="document-preview-fact-edit-icon" />}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {twinLinksSlot && (
              <div className="document-preview-twin-links-slot">{twinLinksSlot}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Render via portal to ensure fullscreen overlay works correctly
  return createPortal(content, document.body);
};

export default DocumentPreview;
